/**
 * queue-rule-groups — group queue elements by their tracker routing RULE (path
 * group). Every queue view (queued drag lists, the segment table, testing) uses
 * this so a Default path and a rule-based path are never lumped together.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { QueueElement, QueueElementRule } from '@trafficpulse/shared';

/** Elements sharing one routing rule (or ungrouped when `rule` is null). */
export interface QueueRuleSection {
  /** Stable key for React + drag scoping — the rule's groupId, or a sentinel. */
  key: string;
  rule: QueueElementRule | null;
  elements: QueueElement[];
}

const UNGROUPED_KEY = '__ungrouped__';

/**
 * Group by routing rule, preserving each element's incoming order within its
 * section. Sections are ordered: the flow's Default bucket first, then
 * rule-based groups by name, then any ungrouped elements last.
 */
export function groupByRule(elements: QueueElement[]): QueueRuleSection[] {
  const sections = new Map<string, QueueRuleSection>();
  for (const el of elements) {
    const key = el.rule ? el.rule.groupId : UNGROUPED_KEY;
    const section = sections.get(key) ?? { key, rule: el.rule ?? null, elements: [] };
    section.elements.push(el);
    sections.set(key, section);
  }
  return [...sections.values()].sort(compareSections);
}

/** Default first, then rule-based by name; ungrouped always last. */
function compareSections(a: QueueRuleSection, b: QueueRuleSection): number {
  if (!a.rule || !b.rule) return a.rule ? -1 : b.rule ? 1 : 0;
  if (a.rule.isDefault !== b.rule.isDefault) return a.rule.isDefault ? -1 : 1;
  return a.rule.name.localeCompare(b.rule.name, undefined, { sensitivity: 'base' });
}

/** Header label for a section: "Default", the rule name, or "No rule". */
export function ruleSectionLabel(section: QueueRuleSection): string {
  if (!section.rule) return 'No rule';
  return section.rule.isDefault ? 'Default' : section.rule.name;
}
